package org.moxultimate.topwithmox.components.buttons

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.px
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.theme.DiscountOrangeColor
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.utils.ColorUtils.hexToColorRgb

/**
 * BuyInOption
 * Holds all data needed to render a single shop row inside a ProductCard.
 *
 * @param logoSrc Path or URL of the platform logo image (e.g. "/ic_shopee.webp").
 * @param price Formatted price string shown in dark text (e.g. "₱ 1,500" or "$ 60").
 * @param discount Discount or promo label shown in orange (e.g. "10%"). Pass empty to hide.
 * @param onClick Callback fired when the "Buy Now" button is pressed.
 */
data class BuyInOption(
    val logoSrc: String,
    val price: String,
    val discount: String,
    val onClick: () -> Unit,
)

/**
 * BuyInButton
 * A full-width shop row: logo on the left (contained, never stretched), price + promo in
 * the centre, and a platform-coloured "Buy Now" pill button on the right.
 *
 * @param option Platform data: logo, price, discount, click handler.
 * @param modifier Optional outer modifier applied to the enclosing Row.
 */
@Composable
fun BuyInButton(
    option: BuyInOption,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        // Platform logo — fixed width matches Shopee's visual footprint across all platforms;
        // height is auto so each logo's natural aspect ratio is preserved without distortion.
        Image(
            src = option.logoSrc,
            description = "Shop logo",
            modifier = Modifier
                .width(70.px)
                .styleModifier {
                    property("height", "auto")
                    property("object-fit", "contain")
                    property("object-position", "left center")
                    property("display", "block")
                },
        )

        // Price + optional promo discount in the centre
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.px),
        ) {
            SpanText(
                text = option.price,
                modifier = Modifier
                    .color(DarkTextColor)
                    .fontFamily(FontFamily)
                    .fontSize(14.px)
                    .fontWeight(FontWeight.Bold),
            )
            if (option.discount.isNotBlank()) {
                SpanText(
                    text = "-${option.discount} promo",
                    modifier = Modifier
                        .color(DiscountOrangeColor)
                        .fontFamily(FontFamily)
                        .fontSize(12.px)
                        .fontWeight(FontWeight.Bold),
                )
            }
        }

        // "Buy Now" pill — colour is derived from the platform inferred by logoSrc
        Box(
            modifier = PressableButtonStyle.toModifier()
                .backgroundColor(platformButtonColor(option.logoSrc))
                .borderRadius(24.px)
                .padding(leftRight = 20.px, topBottom = 10.px)
                .styleModifier { property("cursor", "pointer") }
                .onClick { option.onClick() },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                text = "Buy Now",
                modifier = Modifier
                    .color(Colors.White)
                    .fontFamily(FontFamily)
                    .fontSize(12.px)
                    .fontWeight(FontWeight.SemiBold)
                    .styleModifier { property("white-space", "nowrap") },
            )
        }
    }
}

/**
 * platformButtonColor
 * Maps a logo image path to the platform's brand colour used for the "Buy Now" pill.
 * Falls back to a neutral dark grey when the platform is unrecognised.
 *
 * @param logoSrc Path or URL that identifies the platform (e.g. "/ic_shopee.webp").
 * @return The CSS colour to apply as the pill's background.
 */
private fun platformButtonColor(logoSrc: String): Color {
    val src = logoSrc.lowercase()
    return when {
        "shopee" in src  -> hexToColorRgb("#EE4D2D")   // Shopee orange-red
        "lazada" in src  -> hexToColorRgb("#1B1464")   // Lazada dark navy
        "amazon" in src  -> hexToColorRgb("#FF9900")   // Amazon amber
        "tiktok" in src  -> hexToColorRgb("#000000")   // TikTok black
        else             -> hexToColorRgb("#4A4A4A")   // neutral fallback
    }
}
